import {
  Controller,
  Control,
  FieldPath,
  RegisterOptions,
  FieldValues,
} from 'react-hook-form';
import { useId } from 'react';
import { TextField, Stack, Typography, styled } from '@mui/material';
import { theme } from '~/shared/styles/theme';

interface Props<T extends FieldValues> {
  name: FieldPath<T>;
  control: Control<T>;
  label: string;
  placeholder: string;
  required?: boolean;
  disabled?: boolean;
  rules?: RegisterOptions<T>;
  type?: string;
}

const FormTextField = <T extends FieldValues>({
  name,
  control,
  label,
  placeholder,
  required = false,
  disabled = false,
  rules,
  type = 'text',
}: Props<T>) => {
  const id = useId();
  const labelId = `${name}-${id}`;

  const finalRules: RegisterOptions<T> = required
    ? { required: `${label} is required`, ...rules }
    : rules || {};

  return (
    <Stack width="100%">
      <Label id={labelId} variant="body2">
        {label}
        {required && '*'}
      </Label>
      <Controller
        name={name}
        control={control}
        rules={finalRules}
        render={({ field: { ref, ...field }, fieldState }) => {
          // For number fields, transform the value appropriately
          const handleChange = (newValue: string) => {
            if (type === 'number') {
              // Convert empty strings to undefined for proper validation
              field.onChange(newValue === '' ? undefined : Number(newValue));
            } else {
              field.onChange(newValue);
            }
          };

          const displayValue =
            type === 'number' && field.value === undefined ? '' : field.value;

          return (
            <TextField
              {...field}
              inputRef={ref}
              type={type}
              placeholder={placeholder}
              fullWidth
              required={required}
              disabled={disabled}
              error={Boolean(fieldState.error)}
              helperText={fieldState.error?.message}
              value={displayValue}
              onChange={(e) => handleChange(e.target.value)}
              slotProps={{
                htmlInput: {
                  'aria-labelledby': labelId,
                },
              }}
            />
          );
        }}
      />
    </Stack>
  );
};

export default FormTextField;

const Label = styled(Typography)({
  color: theme.palette.custom.textTitle,
  marginBottom: theme.spacing(1),
});
